import { createRef, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import JoinFormV2ContextProvider, { JoinFormV2Context, LegalContractConsentType } from '../shared/JoinFormV2Context';
import ReCAPTCHA from "react-google-recaptcha";
import { IsNullOrEmpty } from '../../core/utility';
import { post } from '../../core/client';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { CustomButton } from '../../components/buttons';
import { SentimentDissatisfied, TaskAlt, WarningAmber } from '@mui/icons-material';
import reactStringReplace from 'react-string-replace';

interface StateModel {
    tcIdentityNo: string;
    reCaptchaToken: string;
    approvedConsents: Array<string>
}

interface CheckYkbConsentResponse {
    tcIdentityNo?: string;
    success: boolean;
    errorMessage?: string;
}

interface ApproveYkbConsentResponse {
    success: boolean;
    errorMessage: string | null;
}

interface RouteParams {
    encriptedText?: string;
}

const initialState: StateModel = {
    tcIdentityNo: '',
    reCaptchaToken: '',
    approvedConsents: [],
}

enum FetchStatus {
    Loading,
    Success,
    Fail
}

const YkbConsentFormComponent = () => {

    let routeParams: RouteParams = useParams();
    const encriptedText = routeParams.encriptedText ? routeParams.encriptedText : null;

    const { legalTextLoading, legalTexts, loadLegalTexts, openLegalTextModal } = useContext(JoinFormV2Context);

    const recaptchaRef = createRef<any>();

    const [state, setState] = useState<StateModel>(initialState);
    const [sendLoading, setSendLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [validationMessage, setValidationMessage] = useState<string>("");
    const [isSent, setIsSent] = useState(false);
    const [fetchStatus, setFetchStatus] = useState<number>(FetchStatus.Loading);

    useEffect(() => {
        if (!IsNullOrEmpty(encriptedText)) {
            loadPageInfo(encriptedText as string)
        }
    }, [encriptedText]);

    useEffect(() => {
        if (fetchStatus === FetchStatus.Success) {
            loadLegalTexts([
                LegalContractConsentType.YKB_VERI_AKTARIM_IZNI,
                LegalContractConsentType.VERI_IZNI,
                LegalContractConsentType.YKB_AYDINLATMA
            ])
        }
    }, [fetchStatus]);

    const loadPageInfo = async (encriptedText: string) => {
        const request = {
            text: encriptedText
        }
        const { data, fail, errorMessage } = await post<CheckYkbConsentResponse>('api/membership/check-ykb-consent', request);
        if (data) {
            if (data.errorMessage) {
                setErrorMessage(data.errorMessage);
            }
            setState(prev => ({
                ...prev,
                tcIdentityNo: data.tcIdentityNo ?? '',
            }));
            setFetchStatus(FetchStatus.Success);
        }
        if (fail) {
            setFetchStatus(FetchStatus.Fail);
        }
    }

    const handleSend = async (model: StateModel) => {

        if (!validate(model)) {
            resetCaptcha();
            return;
        }

        setSendLoading(true);
        const request = {
            tcIdentityNo: model.tcIdentityNo,
            reCaptchaToken: model.reCaptchaToken
        }
        const { data, fail, errorMessage } = await post<ApproveYkbConsentResponse>('api/membership/approve-ykb-consent', request);
        setSendLoading(false);
        if (data) {
            if (data.errorMessage) {
                setErrorMessage(data.errorMessage);
            }
            else {
                setIsSent(true);
            }
        }

        resetCaptcha();
    }

    const handleYkbAggreement = () => {
        openLegalTextModal(LegalContractConsentType.YKB_AYDINLATMA);
    }

    const handleConsentCheckbox = (e: any) => {
        const consentType = e.target.name;
        let consents: Array<string> = [...state.approvedConsents];
        consents = consents.filter(x => x !== consentType);

        if (e.target.checked) {
            consents = [...consents, consentType];

            if (consentType === LegalContractConsentType.VERI_IZNI
                || consentType === LegalContractConsentType.YURT_DISI_VERI_IZNI
                || consentType === LegalContractConsentType.VAKIF_DERNEK_IZNI
            ) {
                openLegalTextModal(consentType as LegalContractConsentType);
            }
        }
        setState(prev => ({
            ...prev,
            approvedConsents: consents,
        }));
    }
    const isConsentApproved = (type: string) => {
        return state.approvedConsents.some(consent => consent === type);
    }

    const onChangeCaptcha = async (e: any) => {
        setState(prev => ({
            ...prev,
            reCaptchaToken: e
        }));
    }

    const resetCaptcha = () => {
        if (IsNullOrEmpty(state.reCaptchaToken)) return;
        setState(prev => ({ ...prev, reCaptchaToken: '' }));
        (window as any).grecaptcha.reset();
    }

    const isRequiredConsentsChecked = (model: StateModel) => {
        const requiredConsents = legalTexts?.consents.filter(x => x.required
            && x.type !== LegalContractConsentType.UYELIK_SOZLESMESI
            && x.type !== LegalContractConsentType.YKB_AYDINLATMA);
        if (requiredConsents) {

            for (let item of requiredConsents) {
                if (!isConsentApproved(item.type)) {
                    return;
                }
            }
        }
        return true;
    }

    const validate = (model: StateModel) => {
        setValidationMessage("");

        let isValid = true;

        if (!isConsentApproved(LegalContractConsentType.VERI_IZNI) || !isConsentApproved(LegalContractConsentType.YKB_VERI_AKTARIM_IZNI)) {
            setValidationMessage("Lütfen veri aktarım sözleşmelerini onaylayınız.");
            return false;
        }

        if (isValid && IsNullOrEmpty(model.reCaptchaToken)) {
            setValidationMessage("Robot doğrulamasını yapmalısınız.");
            return false;
        }

        return isValid;
    }

    return (
        <div className="join-form-1">
            <div className="overlay">
                <div className="container">
                    {fetchStatus === FetchStatus.Loading ? (
                        <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" paddingY={2}>
                            <p>Lütfen bekleyiniz...</p>
                            <CircularProgress />
                        </Box>
                    ) : fetchStatus === FetchStatus.Fail ? (
                        <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" paddingY={2}>
                            <p>Beklenmeyen bir hata oluştu. Lütfen sistem yöneticisiyle iletişime geçiniz.</p>
                            <SentimentDissatisfied fontSize='large' />
                        </Box>
                    ) : !IsNullOrEmpty(errorMessage) ? (
                        <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" paddingY={2}>
                            <p>{errorMessage}</p>
                            <WarningAmber fontSize='large' />
                        </Box>
                    ) : (
                        <div className="form">
                            {!isSent ? (
                                <>
                                    <h1>YAPI KREDİ BANKASI VERI AKTARIM İZİN FORMU</h1>
                                    {legalTextLoading ? (
                                        <p>Yükleniyor...</p>
                                    ) : (
                                        legalTexts?.consents.filter(x =>
                                            x.type !== LegalContractConsentType.UYELIK_SOZLESMESI
                                            && x.type !== LegalContractConsentType.YKB_AYDINLATMA).map((item, index) => (
                                                <div className="py-1" key={index}>
                                                    <FormControlLabel
                                                        control={<Checkbox
                                                            name={item.type} color="primary"
                                                            checked={isConsentApproved(item.type)}
                                                            onChange={handleConsentCheckbox}
                                                        />}
                                                        label={
                                                            <div dangerouslySetInnerHTML={{ __html: item.text }}></div>
                                                        }
                                                    />
                                                </div>
                                            ))
                                    )}
                                    <Box display="flex" justifyContent="center" marginBottom={2}>
                                        <ReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey="6LdqH0QfAAAAAAQR10C9Poo7LItjCQ0Fbe2l9Fnn"
                                            onChange={onChangeCaptcha}
                                        />
                                    </Box>
                                    <Box marginBottom={2} textAlign="center">
                                        {
                                            reactStringReplace(
                                                legalTexts?.consents.find(x => x.type === LegalContractConsentType.YKB_AYDINLATMA)?.text ?? '',
                                                /<strong>(.*?)<\/strong>/,
                                                (match, i) => (
                                                    <button
                                                        style={{ color: '#177897', cursor: 'pointer', border: 'none' }}
                                                        onClick={handleYkbAggreement}>
                                                        <strong>{match}</strong></button>
                                                ))
                                        }
                                    </Box>
                                    {!IsNullOrEmpty(validationMessage) && (
                                        <Alert severity="error" className="mb-2">{validationMessage}</Alert>
                                    )}

                                    {!IsNullOrEmpty(errorMessage) && (
                                        <Alert severity="error" className="mb-2">{errorMessage}</Alert>
                                    )}
                                    <Box display="flex" justifyContent="center" marginBottom={2}>
                                        <CustomButton
                                            text="ONAYLIYORUM"
                                            className="btn-primary"
                                            disabled={fetchStatus !== FetchStatus.Success || sendLoading || !state.approvedConsents}
                                            loading={sendLoading}
                                            onClick={() => handleSend(state)} />
                                    </Box>
                                </>
                            ) : (
                                <div className="success-container">
                                    <TaskAlt className='success-icon' color='success' />
                                    <p>Yapı Kredi veri aktarımı için onayınız alınmıştır.</p>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    )
}

const YkbConsentForm = () => (
    <JoinFormV2ContextProvider>
        <YkbConsentFormComponent />
    </JoinFormV2ContextProvider>
);

export default YkbConsentForm;